// src/components/registration/steps/userGroupSteps/supportComponents/UserGroup.jsx
import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import MultiStyleButton from "../../../systemUtils/MultiStyleButton";
import LoadingCircleSmall from "../../../../utils/LoadingCircleSmall";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./UserGroup.css";

const isValidEmail = (em) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em.trim());

/* ──────────────────────────────────────────────
   UserGroup (collapsible frame)
────────────────────────────────────────────── */
const UserGroup = ({
  group,
  groupIndex,
  updateUserGroup,
  removeUserGroup,
  addRole,
  removeRole,
  updateRole,
  checkDuplicateEmails,
}) => {
  const roles = Array.isArray(group.roles) ? group.roles : [];

  const [groupName, setGroupName]   = useState(group.groupName);
  const [isEditingGroup, setEdit]   = useState(group.groupName === "");
  const [isCollapsed,  setCollapse] = useState(false);
  const [error, setError]           = useState("");

  const inputRef = useRef(null);

  const handleGroupNameBlur = () => {
    if (!groupName.trim()) {
      setError("Group name is required");
      inputRef.current?.focus();
      return;
    }
    setError("");
    updateUserGroup(groupIndex, "groupName", groupName.trim());
    setEdit(false);
  };

  return (
    <div className="user-group-frame">
      <div className="user-group-header">
        <div
          className="group-header"
          onClick={() => setCollapse(!isCollapsed)}
        >
          <h3 className="group-title">
            {isEditingGroup ? (
              <input
                ref={inputRef}
                type="text"
                value={groupName}
                placeholder="Enter User Group Name"
                onChange={(e) => {
                  setGroupName(e.target.value);
                  if (error) setError("");
                }}
                onBlur={handleGroupNameBlur}
                onKeyDown={(e) => e.key === "Enter" && handleGroupNameBlur()}
                className="group-name-input-inline"
                autoFocus
              />
            ) : (
              group.groupName || "Unnamed Group"
            )}
          </h3>
          <span className="collapse-icon">{isCollapsed ? "+" : "-"}</span>
        </div>
        {!isEditingGroup && (
          <MultiStyleButton
            label="Remove Group"
            buttonType="cancel"
            onClick={() => removeUserGroup(groupIndex)}
          />
        )}
      </div>

      {error && <p className="inline-error">{error}</p>}

      {!isCollapsed && (
        <div className="user-group-content">
          <div className="roles-container">
            <h1 className="roles-item-header">User Roles</h1>
            {roles.length ? (
              roles.map((role, ri) => (
                <RoleItem
                  key={ri}
                  groupIndex={groupIndex}
                  role={role}
                  roleIndex={ri}
                  updateRole={updateRole}
                  removeRole={removeRole}
                  checkDuplicateEmails={checkDuplicateEmails}
                />
              ))
            ) : (
              <p className="no-roles-message">No Roles added yet.</p>
            )}
          </div>
          <div className="add-role-container">
            <MultiStyleButton
              label="Add Role"
              buttonType="approve"
              onClick={() => addRole(groupIndex)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────
   RoleItem (inside a group)
────────────────────────────────────────────── */
const RoleItem = ({
  groupIndex,
  role,
  roleIndex,
  updateRole,
  removeRole,
  checkDuplicateEmails,
}) => {
  const [roleName, setRoleName]     = useState(role.roleName);
  const [isEditingRole, setEdit]    = useState(role.roleName === "");
  const [showAdd, setShowAdd]       = useState(false);
  const [userInput, setUserInput]   = useState("");
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState("");

  const inputRef = useRef(null);

  /* ---- role name validation ---- */
  const handleRoleNameBlur = () => {
    if (!roleName.trim()) {
      setError("Role name is required");
      inputRef.current?.focus();
      return;
    }
    setError("");
    updateRole(groupIndex, roleIndex, "roleName", roleName.trim());
    setEdit(false);
  };

  /* ---- add users ---- */
  const handleAddUser = async () => {
    const emails = userInput
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    /* client-side checks */
    if (!emails.length) {
      setError("Please enter at least one e-mail");
      return;
    }
    if (!emails.every(isValidEmail)) {
      setError("One or more e-mails are invalid");
      return;
    }
    if (!checkDuplicateEmails(emails)) {
      setError("At least one e-mail already exists in another group");
      return;
    }

    setBusy(true);
    try {
      const fn = httpsCallable(getFunctions(), "checkIfUserExists");
      for (const em of emails) {
        const res = await fn({ email: em });
        if (res.data.exists) {
          setError(`User ${em} already exists in the system`);
          setBusy(false);
          return;
        }
      }
      /* success */
      updateRole(groupIndex, roleIndex, "users", [
        ...role.users,
        ...emails,
      ]);
      setUserInput("");
      setShowAdd(false);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Server error; please try again");
    } finally {
      setBusy(false);
    }
  };

  /* ---- JSX ---- */
  return (
    <div className="role-item-usergroupconfig">
      <div className="role-header">
        {isEditingRole ? (
          <input
            ref={inputRef}
            value={roleName}
            placeholder="Enter Role Name"
            onChange={(e) => {
              setRoleName(e.target.value);
              if (error) setError("");
            }}
            onBlur={handleRoleNameBlur}
            onKeyDown={(e) => e.key === "Enter" && handleRoleNameBlur()}
            className="role-name-input-inline"
            autoFocus
          />
        ) : (
          <h4
            className="role-name-usergroupconfig"
            title="Click to edit role name"
            onClick={() => setEdit(true)}
          >
            {role.roleName || "Unnamed Role"}
          </h4>
        )}
        <MultiStyleButton
          label="Remove Role"
          buttonType="cancel"
          onClick={() => removeRole(groupIndex, roleIndex)}
        />
      </div>

      {error && <p className="inline-error">{error}</p>}

      <div className="recently-added-users-box">
        <h5>Users</h5>
        {role.users.length ? (
          <ul>{role.users.map((u, i) => <li key={i}>{u}</li>)}</ul>
        ) : (
          <p className="no-users-message">No users assigned.</p>
        )}
      </div>

      {showAdd ? (
        <div className="add-user-section">
          <input
            className="add-user-input-inline"
            placeholder="Enter e-mails (comma-separated)"
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleAddUser()}
            autoFocus
          />
          <div className="add-user-buttons">
            <MultiStyleButton label="Add"    buttonType="approve" onClick={handleAddUser} disabled={busy}/>
            <MultiStyleButton label="Cancel" buttonType="cancel"  onClick={() => { setShowAdd(false); setUserInput(""); setError(""); }} disabled={busy}/>
          </div>
        </div>
      ) : (
        <div className="add-user-button-container">
          <MultiStyleButton
            label="Add User"
            buttonType="approve"
            onClick={() => { setShowAdd(true); setError(""); }}
          />
        </div>
      )}

      {busy && (
        <div className="loading-indicator">
          <LoadingCircleSmall label="Adding users…" />
        </div>
      )}
    </div>
  );
};

/* ───── prop-types (unchanged) ───── */
RoleItem.propTypes = {
  groupIndex:           PropTypes.number.isRequired,
  roleIndex:            PropTypes.number.isRequired,
  role:                 PropTypes.shape({
    roleName: PropTypes.string.isRequired,
    users:    PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  updateRole:           PropTypes.func.isRequired,
  removeRole:           PropTypes.func.isRequired,
  checkDuplicateEmails: PropTypes.func.isRequired,
};

UserGroup.propTypes = {
  groupIndex:           PropTypes.number.isRequired,
  group:                PropTypes.shape({
    groupName: PropTypes.string.isRequired,
    hierarchy: PropTypes.number.isRequired,
    roles:     PropTypes.arrayOf(
      PropTypes.shape({
        roleName: PropTypes.string.isRequired,
        users:    PropTypes.arrayOf(PropTypes.string).isRequired,
      })
    ).isRequired,
  }).isRequired,
  updateUserGroup:      PropTypes.func.isRequired,
  removeUserGroup:      PropTypes.func.isRequired,
  addRole:              PropTypes.func.isRequired,
  removeRole:           PropTypes.func.isRequired,
  updateRole:           PropTypes.func.isRequired,
  checkDuplicateEmails: PropTypes.func.isRequired,
};

export default UserGroup;
